import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  getVectorStats,
  searchVectors,
  purgeVectors,
  listVectors,
  deleteVectors,
  type VectorSearchResult,
  type VectorIndexEntry,
} from "@/lib/api";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
// Note: Select was removed - using tabs for source filter now
import {
  Search,
  Loader2,
  ExternalLink,
  FileText,
  Trash2,
  BookOpen,
  MessageSquare,
  Layers,
  FileIcon,
  ChevronLeft,
  ChevronRight,
  List,
  Database,
  AlertTriangle,
} from "lucide-react";
import { cn, formatDate, formatDateTime } from "@/lib/utils";

// Skeleton row component for smooth loading transitions
function VectorRowSkeleton() {
  return (
    <div className="grid grid-cols-[40px_1fr_100px_120px_80px] gap-2 p-3 border-b last:border-0 items-center animate-pulse">
      <div className="flex items-center">
        <div className="h-4 w-4 bg-muted rounded" />
      </div>
      <div className="min-w-0 space-y-2">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-1/2" />
      </div>
      <div>
        <div className="h-5 bg-muted rounded w-16" />
      </div>
      <div>
        <div className="h-3 bg-muted rounded w-20" />
      </div>
      <div className="flex items-center gap-1">
        <div className="h-6 w-6 bg-muted rounded" />
        <div className="h-6 w-6 bg-muted rounded" />
      </div>
    </div>
  );
}

type SourceType = "slack" | "notion" | "help" | "linear";

const sourceConfig: Record<SourceType, { icon: typeof BookOpen; color: string; bgColor: string; label: string }> = {
  help: { icon: BookOpen, color: "text-blue-500", bgColor: "bg-blue-500/10", label: "Help center" },
  slack: { icon: MessageSquare, color: "text-purple-500", bgColor: "bg-purple-500/10", label: "Slack" },
  linear: { icon: Layers, color: "text-indigo-500", bgColor: "bg-indigo-500/10", label: "Linear" },
  notion: { icon: FileIcon, color: "text-orange-500", bgColor: "bg-orange-500/10", label: "Notion" },
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

export function VectorsPage() {
  const queryClient = useQueryClient();

  // Tab state
  const [activeTab, setActiveTab] = useState<"list" | "search">("list");

  // List state
  const [sourceFilter, setSourceFilter] = useState<SourceType | "all">("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewingVector, setViewingVector] = useState<VectorIndexEntry | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Search state
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<VectorSearchResult[]>([]);

  // Queries
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["vector-stats"],
    queryFn: getVectorStats,
  });

  const { data: vectorList, isLoading: listLoading, isFetching: listFetching, isPlaceholderData } = useQuery({
    queryKey: ["vector-list", sourceFilter, page, pageSize],
    queryFn: () => listVectors({
      source: sourceFilter === "all" ? undefined : sourceFilter,
      limit: pageSize,
      offset: page * pageSize,
    }),
    enabled: activeTab === "list",
    placeholderData: keepPreviousData, // Keep previous data visible while fetching new page
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  // Mutations
  const searchMutation = useMutation({
    mutationFn: (q: string) => searchVectors(q, 20),
    onSuccess: (data) => setSearchResults(data),
  });

  const purgeMutation = useMutation({
    mutationFn: (source: SourceType | "all") => purgeVectors(source),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vector-stats"] });
      queryClient.invalidateQueries({ queryKey: ["vector-list"] });
      setSelectedIds(new Set());
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => deleteVectors(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vector-stats"] });
      queryClient.invalidateQueries({ queryKey: ["vector-list"] });
      setSelectedIds(new Set());
      setDeleteConfirmOpen(false);
    },
  });

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      searchMutation.mutate(query);
    }
  };

  const handlePurge = (source: SourceType) => {
    if (confirm(`Are you sure you want to purge all ${source} vectors? This cannot be undone.`)) {
      purgeMutation.mutate(source);
    }
  };

  const handleSelectAll = () => {
    if (!vectorList?.vectors) return;
    const allIds = vectorList.vectors.map(v => v.id);
    if (selectedIds.size === allIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size > 0) {
      setDeleteConfirmOpen(true);
    }
  };

  const confirmDelete = () => {
    deleteMutation.mutate(Array.from(selectedIds));
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case "help":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100";
      case "slack":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100";
      case "linear":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100";
      case "notion":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100";
      default:
        return "";
    }
  };

  const sources: SourceType[] = ["help", "slack", "linear", "notion"];
  const totalPages = vectorList ? Math.ceil(vectorList.total / pageSize) : 0;

  // Source filter tabs configuration
  const sourceTabOptions = [
    { value: "all" as const, label: "All sources" },
    ...sources.map((s) => ({ value: s, label: sourceConfig[s].label })),
  ];

  // Handle page size change (reset to first page)
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0);
    setSelectedIds(new Set());
  };

  // Page input state
  const [pageInput, setPageInput] = useState("");

  // Handle page input change
  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  // Handle page input submit
  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newPage = parseInt(pageInput, 10);
    if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
      setPage(newPage - 1);
      setPageInput("");
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const currentPage = page + 1;

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("ellipsis");
      }

      // Show pages around current
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("ellipsis");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  // Reusable pagination component with loading indicator
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const showLoadingIndicator = listFetching && isPlaceholderData;
    const pageNumbers = getPageNumbers();

    return (
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-4">
          <span className={cn(
            "text-sm text-muted-foreground transition-opacity duration-200",
            showLoadingIndicator && "opacity-50"
          )}>
            {page * pageSize + 1} - {Math.min((page + 1) * pageSize, vectorList?.total || 0)} of{" "}
            {vectorList?.total.toLocaleString()}
          </span>
          {showLoadingIndicator && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="h-8 rounded border bg-background px-2 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || listFetching}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page number buttons */}
          {pageNumbers.map((pageNum, idx) =>
            pageNum === "ellipsis" ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">...</span>
            ) : (
              <Button
                key={pageNum}
                variant={page === pageNum - 1 ? "default" : "outline"}
                size="sm"
                className="min-w-[32px]"
                onClick={() => setPage(pageNum - 1)}
                disabled={listFetching}
              >
                {pageNum}
              </Button>
            )
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || listFetching}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Go to page input */}
          <form onSubmit={handlePageInputSubmit} className="flex items-center gap-1 ml-2">
            <Input
              type="number"
              min={1}
              max={totalPages}
              placeholder="Go to"
              value={pageInput}
              onChange={handlePageInputChange}
              className="w-16 h-8 text-sm"
            />
            <Button
              type="submit"
              variant="outline"
              size="sm"
              disabled={!pageInput || listFetching}
            >
              Go
            </Button>
          </form>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Vector Management</h1>
          <p className="text-muted-foreground mt-1">
            Browse, search, and manage your vector database
          </p>
        </div>

        {/* Stats - 4 source cards with purge buttons */}
        <div className="grid gap-4 md:grid-cols-4">
          {sources.map((source) => {
            const config = sourceConfig[source];
            const Icon = config.icon;
            const count = stats?.bySource?.[source] ?? 0;
            const isPurging = purgeMutation.isPending && purgeMutation.variables === source;

            return (
              <Card key={source}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <div className={`p-1 rounded ${config.bgColor} ${config.color}`}>
                      <Icon className="h-3 w-3" />
                    </div>
                    <span className="capitalize">{source}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold">
                      {statsLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        count.toLocaleString()
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handlePurge(source)}
                      disabled={isPurging || count === 0}
                      title={`Purge all ${source} vectors`}
                    >
                      {isPurging ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Status messages */}
        {purgeMutation.isSuccess && (
          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-700 dark:text-green-400 text-sm">
            Successfully purged vectors. Trigger a new sync to re-index.
          </div>
        )}
        {purgeMutation.isError && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
            Failed to purge vectors. Please try again.
          </div>
        )}
        {deleteMutation.isSuccess && (
          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-700 dark:text-green-400 text-sm">
            Successfully deleted {deleteMutation.data?.deleted} vector(s).
          </div>
        )}
        {deleteMutation.isError && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
            Failed to delete vectors. Please try again.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab("list")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === "list"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <List className="h-4 w-4" />
            Browse Vectors
          </button>
          <button
            onClick={() => setActiveTab("search")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === "search"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Search className="h-4 w-4" />
            Semantic Search
          </button>
        </div>

        {/* Browse Tab */}
        {activeTab === "list" && (
          <Card>
            <CardHeader className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Vector Index
                    <Badge
                      variant="secondary"
                      className={cn(
                        "ml-2 transition-opacity duration-200",
                        !vectorList && "opacity-0"
                      )}
                    >
                      {vectorList?.total.toLocaleString() ?? "0"} total
                    </Badge>
                  </CardTitle>
                  {/* Source filter tabs - inline with title */}
                  <div className="flex gap-1 rounded-lg bg-muted p-1">
                    {sourceTabOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSourceFilter(option.value);
                          setPage(0);
                          setSelectedIds(new Set());
                        }}
                        className={cn(
                          "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                          sourceFilter === option.value
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Delete button - always in right column, reserve space even when hidden */}
                <div className="min-w-[140px] flex justify-end">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteSelected}
                    disabled={deleteMutation.isPending || selectedIds.size === 0}
                    className={cn(
                      "transition-opacity duration-200",
                      selectedIds.size === 0 && "opacity-0 pointer-events-none"
                    )}
                  >
                    {deleteMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Delete ({selectedIds.size})
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Initial load - show skeleton rows */}
              {listLoading && !vectorList ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[40px_1fr_100px_120px_80px] gap-2 p-3 bg-muted/50 text-sm font-medium text-muted-foreground border-b">
                    <div className="flex items-center">
                      <div className="h-4 w-4 bg-muted rounded" />
                    </div>
                    <div>Content</div>
                    <div>Source</div>
                    <div>Created</div>
                    <div>Actions</div>
                  </div>
                  {Array.from({ length: pageSize > 10 ? 10 : pageSize }).map((_, i) => (
                    <VectorRowSkeleton key={i} />
                  ))}
                </div>
              ) : vectorList?.vectors.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No vectors found.</p>
                  <p className="text-sm mt-1">
                    {sourceFilter !== "all"
                      ? `Try selecting a different source or run a ${sourceFilter} sync.`
                      : "Run a sync to populate the vector database."}
                  </p>
                </div>
              ) : (
                <>
                  {/* Top Pagination */}
                  {renderPagination()}

                  {/* Table with smooth transitions */}
                  <div className="border rounded-lg overflow-hidden relative">
                    {/* Loading overlay - shows when fetching new page with stale data visible */}
                    {listFetching && isPlaceholderData && (
                      <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-10 flex items-center justify-center pointer-events-none transition-opacity duration-200">
                        <div className="bg-background/90 rounded-lg px-4 py-2 shadow-lg flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm text-muted-foreground">Loading...</span>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-[40px_1fr_100px_120px_80px] gap-2 p-3 bg-muted/50 text-sm font-medium text-muted-foreground border-b">
                      <div
                        className="flex items-center cursor-pointer"
                        onClick={handleSelectAll}
                      >
                        <Checkbox
                          checked={
                            vectorList?.vectors.length === selectedIds.size &&
                            selectedIds.size > 0
                          }
                          onCheckedChange={handleSelectAll}
                        />
                      </div>
                      <div>Content</div>
                      <div>Source</div>
                      <div>Created</div>
                      <div>Actions</div>
                    </div>

                    {/* Table rows with transition */}
                    <div className={cn(
                      "transition-opacity duration-150",
                      listFetching && isPlaceholderData && "opacity-60"
                    )}>
                      {vectorList?.vectors.map((vector) => (
                        <div
                          key={vector.id}
                          className="grid grid-cols-[40px_1fr_100px_120px_80px] gap-2 p-3 border-b last:border-0 hover:bg-muted/30 transition-colors items-center"
                        >
                          <div
                            className="flex items-center cursor-pointer"
                            onClick={(e) => {
                              if ((e.target as HTMLElement).tagName !== "INPUT") {
                                handleSelectOne(vector.id);
                              }
                            }}
                          >
                            <Checkbox
                              checked={selectedIds.has(vector.id)}
                              onCheckedChange={() => handleSelectOne(vector.id)}
                            />
                          </div>
                          <div className="min-w-0">
                            <div
                              className="font-medium text-sm truncate cursor-pointer hover:text-primary transition-colors"
                              onClick={() => setViewingVector(vector)}
                              title={vector.title || vector.id}
                            >
                              {vector.title || vector.id}
                            </div>
                            <div className="text-xs text-muted-foreground truncate font-mono">
                              {vector.id}
                            </div>
                          </div>
                          <div>
                            <Badge className={getSourceColor(vector.source)}>
                              {sourceConfig[vector.source as SourceType]?.label || vector.source}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatDate(vector.createdAt)}
                          </div>
                          <div className="flex items-center gap-1">
                            {vector.url && (
                              <a
                                href={vector.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                                title="Open source"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}
                            <button
                              onClick={() => {
                                setSelectedIds(new Set([vector.id]));
                                setDeleteConfirmOpen(true);
                              }}
                              className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                              title="Delete vector"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom Pagination */}
                  {renderPagination()}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Search Tab */}
        {activeTab === "search" && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Semantic Search
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearch} className="flex gap-2">
                  <Input
                    placeholder="Enter a search query to find similar vectors..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={searchMutation.isPending}>
                    {searchMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Search"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Results */}
            {searchResults.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Search Results ({searchResults.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {searchResults.map((result, idx) => (
                      <div
                        key={result.id}
                        className="border rounded-lg p-4 space-y-2"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-muted-foreground">
                              #{idx + 1}
                            </span>
                            <Badge className={getSourceColor(result.source)}>
                              {result.source}
                            </Badge>
                            <Badge variant="outline">
                              {(result.score * 100).toFixed(1)}% match
                            </Badge>
                          </div>
                          {result.url && (
                            <a
                              href={result.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>

                        <p className="text-sm line-clamp-3">{result.content}</p>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <FileText className="h-3 w-3" />
                          <span className="font-mono truncate max-w-xs">
                            {result.id}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {searchMutation.isError && (
              <Card className="border-destructive">
                <CardContent className="pt-6">
                  <p className="text-destructive text-sm">
                    Search failed. Please try again.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* View Vector Dialog */}
        <Dialog open={!!viewingVector} onOpenChange={() => setViewingVector(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Vector Details
              </DialogTitle>
            </DialogHeader>
            {viewingVector && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">ID</label>
                  <p className="font-mono text-sm mt-1 p-2 bg-muted rounded">
                    {viewingVector.id}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Source</label>
                    <div className="mt-1">
                      <Badge className={getSourceColor(viewingVector.source)}>
                        {viewingVector.source}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created</label>
                    <p className="text-sm mt-1">
                      {formatDateTime(viewingVector.createdAt)}
                    </p>
                  </div>
                </div>
                {viewingVector.title && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Title</label>
                    <p className="text-sm mt-1">{viewingVector.title}</p>
                  </div>
                )}
                {viewingVector.contentPreview && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Content Preview
                    </label>
                    <p className="text-sm mt-1 p-2 bg-muted rounded whitespace-pre-wrap">
                      {viewingVector.contentPreview}
                    </p>
                  </div>
                )}
                {viewingVector.url && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">URL</label>
                    <a
                      href={viewingVector.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm mt-1 text-primary hover:underline flex items-center gap-1"
                    >
                      {viewingVector.url}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewingVector(null)}>
                Close
              </Button>
              {viewingVector?.url && (
                <a href={viewingVector.url} target="_blank" rel="noopener noreferrer">
                  <Button>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Source
                  </Button>
                </a>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Confirm Deletion
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {selectedIds.size} vector(s)? This action cannot
                be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
